import { Gamepad2, ChevronDown } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

interface CategoryCollapsibleMenuItemProps {
	icon: React.ComponentType<{ className?: string }> | string | null
	label: string
	count?: number
	isActive: boolean
	open: boolean
	onOpenChange: (open: boolean) => void
	children: React.ReactNode
}

export function CategoryCollapsibleMenuItem({ icon, label, count, isActive, open, onOpenChange, children }: CategoryCollapsibleMenuItemProps) {
	const Icon = typeof icon == 'function' || typeof icon === 'object' ? icon : null

	return (
		<Collapsible open={open} onOpenChange={onOpenChange} className='flex flex-col gap-1'>
			<div className={cn('group overflow-hidden rounded-xl border p-0.5 transition-colors', isActive ? 'border-primary' : 'border-border')}>
				<CollapsibleTrigger asChild>
					<button
						type='button'
						className={cn(
							'flex w-full items-center justify-between gap-2 overflow-hidden rounded-[10px] py-2 pl-1 pr-3.5 text-left transition-colors',
							isActive ? 'bg-secondary bg-[linear-gradient(90deg,hsl(var(--primary)/0.12)_1.15%,hsl(var(--primary)/0)_82.95%)]' : 'bg-secondary',
						)}
					>
						<span className='flex min-w-0 items-center gap-2'>
							<span
								aria-hidden
								className={cn(
									'h-4 w-0.5 shrink-0 rounded-full transition-[background-color,box-shadow]',
									isActive
										? 'bg-primary shadow-[0_0_12px_6px_hsl(var(--primary)/0.6)]'
										: 'bg-border group-hover:bg-primary group-hover:shadow-[0_0_8px_2px_hsl(var(--primary)/0.35)]',
								)}
							/>
							{Icon ? (
								<Icon className={cn('h-6 w-6 shrink-0 transition-colors', isActive ? 'text-primary' : 'text-icon-muted group-hover:text-primary')} />
							) : icon ? (
								<span
									aria-hidden
									className={cn(
										'h-6 w-6 shrink-0 bg-current transition-colors [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]',
										isActive ? 'text-primary' : 'text-icon-muted group-hover:text-primary',
									)}
									style={{
										maskImage: `url(${icon})`,
										WebkitMaskImage: `url(${icon})`,
									}}
								/>
							) : (
								<Gamepad2 className={cn('h-6 w-6 shrink-0 transition-colors', isActive ? 'text-primary' : 'text-icon-muted group-hover:text-primary')} />
							)}
							<span
								className={cn(
									'truncate text-sm tracking-[-0.21px] transition-colors',
									isActive ? 'font-medium text-foreground' : 'font-normal text-muted-foreground',
								)}
							>
								{label}
							</span>
						</span>
						<span className='flex shrink-0 items-center gap-2'>
							{typeof count === 'number' ? <span className='text-sm font-normal tracking-[-0.21px] text-muted-foreground'>{count}</span> : null}
							<ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} aria-hidden />
						</span>
					</button>
				</CollapsibleTrigger>
			</div>

			<CollapsibleContent className='data-[state=closed]:hidden'>
				<div className='flex flex-col gap-1 pl-3'>{children}</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
